/**
 * TimeSlotBlock - Displays a scheduled workout as a block in timeline views
 */

import { motion } from 'framer-motion';
import { Clock, Check, X } from 'lucide-react';
import { MUSCLE_GROUPS } from '../../stores/fitnessStore';

// Status colors
const STATUS_COLORS = {
  scheduled: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-400' },
  in_progress: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', text: 'text-yellow-400' },
  completed: { bg: 'bg-green-500/20', border: 'border-green-500/50', text: 'text-green-400' },
  missed: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400' },
  cancelled: { bg: 'bg-gray-500/20', border: 'border-gray-500/50', text: 'text-gray-400' },
};

// Exercise type config
const TYPE_CONFIG = {
  fitness: { label: 'Fitness', emoji: '🏋️', color: 'bg-blue-500' },
  kickbox: { label: 'Kickbox', emoji: '🥊', color: 'bg-red-500' },
  cardio: { label: 'Kardiyo', emoji: '🏃', color: 'bg-green-500' },
};

const TimeSlotBlock = ({ workout, expanded = false, onClick }) => {
  const status = workout.status || 'scheduled';
  const colors = STATUS_COLORS[status] || STATUS_COLORS.scheduled;
  const typeConfig = TYPE_CONFIG[workout.exercise_type] || TYPE_CONFIG.fitness;
  const muscleLabel = workout.muscle_group && MUSCLE_GROUPS[workout.muscle_group]?.label;

  // Check if this is from coach's all-clients view (has client_name or client_email)
  const clientDisplayName = workout.client_name || workout.client_email || '';
  const hasClientInfo = !!clientDisplayName.trim();

  // Check if this workout was created by a coach (for student view)
  const coachDisplayName = workout.coach_name || '';
  const hasCoachInfo = !!coachDisplayName.trim() && !hasClientInfo; // Only show coach if not showing client

  // Get short names (first name or email prefix)
  const shortClientName = clientDisplayName
    ? clientDisplayName.split(' ')[0].split('@')[0]
    : null;
  const shortCoachName = coachDisplayName
    ? coachDisplayName.split(' ')[0].split('@')[0]
    : null;

  // Get initials for fallback avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const clientInitials = getInitials(clientDisplayName);
  const coachInitials = getInitials(coachDisplayName);

  // Generate display label
  const displayLabel = workout.exercise_type === 'fitness' && muscleLabel
    ? `${typeConfig.label} - ${muscleLabel}`
    : typeConfig.label;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        ${colors.bg} ${colors.border} border rounded-lg cursor-pointer
        transition-all hover:shadow-lg
        ${expanded ? 'p-3' : 'p-2'}
      `}
    >
      <div className="flex items-start gap-2">
        {/* Type indicator */}
        <div className={`w-2 h-2 rounded-full ${typeConfig.color} mt-1.5 flex-shrink-0`} />

        <div className="flex-1 min-w-0">
          {/* Client name (if viewing all clients - coach view) */}
          {hasClientInfo && (
            <div className="flex items-center gap-1.5 text-xs text-orange-400 font-medium truncate mb-0.5">
              {workout.client_avatar_url ? (
                <img
                  src={workout.client_avatar_url}
                  alt={shortClientName}
                  className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-4 h-4 rounded-full bg-orange-500/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xs font-bold text-orange-400">{clientInitials}</span>
                </div>
              )}
              {expanded ? clientDisplayName : shortClientName}
            </div>
          )}

          {/* Coach name (if created by coach - student view) */}
          {hasCoachInfo && (
            <div className="flex items-center gap-1.5 text-xs text-violet-400 font-medium truncate mb-0.5">
              {workout.coach_avatar_url ? (
                <img
                  src={workout.coach_avatar_url}
                  alt={shortCoachName}
                  className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-4 h-4 rounded-full bg-violet-500/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xs font-bold text-violet-400">{coachInitials}</span>
                </div>
              )}
              {expanded ? coachDisplayName : shortCoachName}
            </div>
          )}

          {/* Type and muscle group */}
          <div className={`font-medium text-sm ${colors.text} truncate`}>
            {expanded ? displayLabel : typeConfig.emoji}
          </div>

          {/* Time and duration (if expanded) */}
          {expanded && (
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              {workout.scheduled_time?.substring(0, 5)}
              {workout.duration_minutes && (
                <span className="ml-1">({workout.duration_minutes} dk)</span>
              )}
            </div>
          )}

          {/* Notes preview (if expanded and has notes) */}
          {expanded && workout.notes && (
            <div className="mt-1 text-2xs text-gray-500 truncate">
              {workout.notes}
            </div>
          )}
        </div>

        {/* Status icon */}
        {status === 'completed' && (
          <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
        )}
        {status === 'missed' && (
          <X className="w-4 h-4 text-red-400 flex-shrink-0" />
        )}
      </div>
    </motion.div>
  );
};

export default TimeSlotBlock;
